'use client';

import Link from 'next/link';
import { trackCTAClick, trackNavClick } from '@/lib/analytics';

/**
 * TrackedLink — wraps Next.js Link with analytics tracking
 *
 * Usage for CTA buttons:
 *   <TrackedLink href="/products" type="cta" ctaText="View Products" ctaSection="hero">
 *     View Products
 *   </TrackedLink>
 *
 * Usage for navigation:
 *   <TrackedLink href="/about" type="nav">
 *     About Us
 *   </TrackedLink>
 */
export function TrackedLink({
    href,
    children,
    type = 'nav',
    ctaText,
    ctaSection,
    ctaType = 'primary',
    navType = 'main_header',
    className,
    onClick,
    ...props
}) {
    // Validate required CTA fields in development
    if (type === 'cta' && !ctaSection && typeof window !== 'undefined') {
        const isDev = process.env.NODE_ENV !== 'production';
        if (isDev) {
            // eslint-disable-next-line no-console
            console.warn(
                `TrackedLink: CTA link missing ctaSection. This should be specified for proper analytics tracking. Text: "${ctaText || children}"`,
            );
        }
    }

    const handleClick = (e) => {
        if (type === 'cta') {
            const text =
                ctaText || (typeof children === 'string' ? children : '');
            trackCTAClick(text, {
                cta_section: ctaSection || 'unspecified',
                cta_type: ctaType,
            });
        } else if (type === 'nav') {
            const text = typeof children === 'string' ? children : '';
            trackNavClick(text, href, {
                nav_type: navType,
            });
        }

        onClick?.(e);
    };

    return (
        <Link
            href={href}
            className={className}
            onClick={handleClick}
            {...props}
        >
            {children}
        </Link>
    );
}

export default TrackedLink;
